import { useI18n } from '@/hooks/useI18n';
import { Eye, Flame, Layers, Users } from 'lucide-react';
import { Chip } from '@/components/primitives/Chip';
import { proxyImage } from '@/lib/imageProxy';
import { formatNumber, formatPct, formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { TemplateSummary } from '@/types/api';

interface TemplateRowProps {
  template: TemplateSummary;
  onClick: () => void;
}

export function TemplateRow({ template, onClick }: TemplateRowProps) {
  const { t } = useI18n();
  const preview = template.captionPreview ?? t('(Sans caption)', '(No caption)');
  const thumbnail = template.medianReelThumbnailUrl;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group grid w-full min-w-0 grid-cols-[66px_minmax(0,1fr)] items-stretch gap-3 rounded-[14px] border border-line-1 bg-bg-1 p-3 text-left shadow-card transition-all sm:gap-x-4',
        'hover:border-line-2 hover:shadow-card-hover',
      )}
    >
      <div className="relative h-[88px] w-[66px] shrink-0 overflow-hidden rounded-[10px] bg-bg-2 sm:row-span-2">
        {thumbnail ? (
          <img
            src={proxyImage(thumbnail)}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <Placeholder seed={template.templateHash} />
        )}
        <span className="absolute inset-x-1.5 bottom-1.5 flex items-center gap-1 rounded-full bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm">
          <Eye className="h-2.5 w-2.5" />
          {formatNumber(template.medianViews)}
        </span>
      </div>

      <div className="min-w-0">
        <div className="space-y-1">
          <p className="line-clamp-2 break-words text-[13px] leading-snug text-text-1">{preview}</p>
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-text-3">
            <Chip>
              <Layers className="h-2.5 w-2.5" />
              {template.reelCount} reels
            </Chip>
            <Chip>
              <Users className="h-2.5 w-2.5" />
              {template.accountCount}{' '}
              {template.accountCount > 1 ? t('comptes', 'accounts') : t('compte', 'account')}
            </Chip>
            {template.viralCount > 0 && (
              <Chip tone="violet">
                <Flame className="h-2.5 w-2.5" />
                {template.viralCount} viral ·{' '}
                {formatPct(template.viralRate, { sign: false, digits: 0 })}
              </Chip>
            )}
            <span className="w-full text-[11px] text-text-4 sm:ml-auto sm:w-auto">
              {t('Dernier', 'Latest')} · {formatRelative(template.lastPublishedAt)}
            </span>
          </div>
        </div>
      </div>

      <dl className="col-span-2 grid min-w-0 grid-cols-3 gap-2 border-t border-line-1 pt-2 text-[12px] text-text-3 sm:col-span-1 sm:col-start-2 sm:gap-3">
        <Metric label={t('Médiane', 'Median')} value={formatNumber(template.medianViews)} hero />
        <Metric label={t('Meilleur', 'Best')} value={formatNumber(template.maxViews)} />
        <Metric label="Minimum" value={formatNumber(template.minViews)} />
      </dl>
    </button>
  );
}

function Metric({ label, value, hero }: { label: string; value: string; hero?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-medium uppercase tracking-[0.08em] text-text-4 sm:tracking-[0.16em]">
        {label}
      </dt>
      <dd
        className={cn(
          'font-display tabular-nums',
          hero ? 'text-[18px] text-text-1' : 'text-[15px] text-text-2',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function Placeholder({ seed }: { seed: string }) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return (
    <div
      className="absolute inset-0"
      style={{
        background: `linear-gradient(135deg, hsl(${hue} 65% 85%), hsl(${(hue + 50) % 360} 65% 93%))`,
      }}
    />
  );
}
